// ============================================================
// useIsOrganizer — ログインユーザの「管理者」権限(OWNER + MEMBER)判定
// ============================================================
//
// /api/auth/me の isOrganizer / orgMemberships を fetch して保持する。
// 同一トークン中はモジュール内キャッシュで重複フェッチ抑止。
// OrganizerRoute・AdminLayout・編集ボタン表示判定で共有。
//
// 戻り値:
//   - null     : 判定中（ローディング）
//   - true     : 管理者(OWNER+MEMBER)で少なくとも 1 団体に所属
//   - false    : 管理者でない(RESULT_ONLY のみ または 所属なし)
//
// 関連:
//   useOrgMemberships() : 全所属団体のロールマップを返す。per-event 編集可否判定に使用

import { useEffect, useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/services/api'

export type OrgRole = 'OWNER' | 'MEMBER' | 'RESULT_ONLY'
export type OrgMembership = { organizationId: number; role: OrgRole }

type CacheEntry = {
  token:          string
  isOrganizer:    boolean
  orgMemberships: OrgMembership[]
}
let cache: CacheEntry | null = null

/** モジュール外からキャッシュをクリアしたい場合に呼ぶ（連携状態が変わった直後など） */
export function clearIsOrganizerCache() {
  cache = null
}

function fetchMe(token: string, setter: (entry: CacheEntry) => void): () => void {
  let cancelled = false
  authApi.me()
    .then((me) => {
      if (cancelled) return
      const entry: CacheEntry = {
        token,
        isOrganizer:    me.isOrganizer,
        orgMemberships: (me.orgMemberships ?? []) as OrgMembership[],
      }
      cache = entry
      setter(entry)
    })
    .catch(() => {
      if (cancelled) return
      const entry: CacheEntry = { token, isOrganizer: false, orgMemberships: [] }
      setter(entry)
    })
  return () => { cancelled = true }
}

/**
 * 任意の団体に所属しているか(RESULT_ONLY 含む)。
 * /events リスト・/events/:id 詳細など「閲覧+結果入力」までを許可する画面のガードに使用。
 */
export function useHasAnyOrgMembership(): boolean | null {
  const token = useAuthStore((s) => s.token)
  const [value, setValue] = useState<boolean | null>(() => {
    if (!token) return false
    if (cache && cache.token === token) return cache.orgMemberships.length > 0
    return null
  })
  useEffect(() => {
    if (!token) { setValue(false); return }
    if (cache && cache.token === token) { setValue(cache.orgMemberships.length > 0); return }
    return fetchMe(token, (entry) => setValue(entry.orgMemberships.length > 0))
  }, [token])
  return value
}

export function useIsOrganizer(): boolean | null {
  const token = useAuthStore((s) => s.token)
  const [value, setValue] = useState<boolean | null>(() => {
    if (!token) return false
    if (cache && cache.token === token) return cache.isOrganizer
    return null
  })
  useEffect(() => {
    if (!token) { setValue(false); return }
    if (cache && cache.token === token) { setValue(cache.isOrganizer); return }
    return fetchMe(token, (entry) => setValue(entry.isOrganizer))
  }, [token])
  return value
}

/**
 * 自分の所属団体ロール一覧を返す。
 * - null: ロード中
 * - 配列: 所属団体ごとの { organizationId, role }（空配列なら無所属）
 *
 * 用途: イベント単位の編集可否判定。
 *   const memberships = useOrgMemberships()
 *   const myRole = memberships?.find((m) => m.organizationId === event.organizationId)?.role
 *   const canEdit = myRole === 'OWNER' || myRole === 'MEMBER'
 */
export function useOrgMemberships(): OrgMembership[] | null {
  const token = useAuthStore((s) => s.token)
  const [value, setValue] = useState<OrgMembership[] | null>(() => {
    if (!token) return []
    if (cache && cache.token === token) return cache.orgMemberships
    return null
  })
  useEffect(() => {
    if (!token) { setValue([]); return }
    if (cache && cache.token === token) { setValue(cache.orgMemberships); return }
    return fetchMe(token, (entry) => setValue(entry.orgMemberships))
  }, [token])
  return value
}

/**
 * イベント単位の編集可否判定。
 * - event.organizationId と自分のメンバーシップから OWNER/MEMBER かどうかを判定。
 * - null: 判定中(ロード中)、true: 編集可、false: 編集不可(RESULT_ONLY または非メンバー)
 */
export function useEventCanEdit(eventOrgId: number | null | undefined): boolean | null {
  const memberships = useOrgMemberships()
  if (memberships === null) return null
  if (eventOrgId === null || eventOrgId === undefined) return false
  const myRole = memberships.find((m) => m.organizationId === eventOrgId)?.role
  return myRole === 'OWNER' || myRole === 'MEMBER'
}
